import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ModeStandbyOutlined, People, PersonOff } from "@mui/icons-material";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getUserTableColumns } from "../../pages/administration/constants";
import { AdminService } from "../../services/admin.service";
import { RolesService } from "../../services";
import { DELETE_USER, EDIT_USER, EXPORT_USER } from "../../constants";
import { AddEditUser } from "./AddEditUser.component";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { formatRupees } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const UsersList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, onSuccess = () => {}, reloadData }) => {
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "users",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [openModal, setOpenModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
  });

  let navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.user);

  const hasUserEdit = useHasPermission(EDIT_USER);
  const hasUserDelete = useHasPermission(DELETE_USER);
  const hasUserExport = useHasPermission(EXPORT_USER);

  const fetchData = useCallback(
    async (userFilters = {}) => {
      setLoading(true);
      try {
        const response = await AdminService.getUserList({
          ...userFilters,
          type: "USER",
          includeBalance: true,
          ...(companyId !== null && { companyId }),
        });
        const usersWithFormattedBalance = response?.data?.rows?.map((user) => ({
          ...user,
          accounts: user.accounts?.map((account) => ({
            ...account,
            formattedBalance: formatRupees(account.balance),
          })),
        }));

        setUsers(usersWithFormattedBalance);
        setTotalCount(response?.data?.count);

        const roleIds = Array.from(new Set(response?.data?.rows?.map((item) => item.roleId)));
        if (roleIds.length > 0) {
          const roleResponse = await RolesService.getRoles({
            ids: roleIds.join(","),
            limit: roleIds.length,
            includeDeleted: true,
            ...(companyId !== null && { companyId }),
          });
          setUserRoles(roleResponse?.data?.rows);
        }
      } catch (error) {
        toast.error("Failed to fetch user list");
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(getUserTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Users : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={currentUser?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Users : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/user/${params.row.id}`);
  };

  const handleOpenModal = (userData) => {
    setUserId(userData.id);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = (userData) => {
    setDeleteModal({
      isOpen: true,
      userId: userData.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      userId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await AdminService.deleteUser(deleteModal.userId);
      toast.success("User deleted successfully");
      closeDeleteModal();
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleActions = (userData, actionType) => {
    if (actionType === "edit") {
      handleOpenModal(userData);
    } else if (actionType === "delete") {
      openDeleteModal(userData);
    }
  };

  const columnHeaders = getUserTableColumns(handleActions, currentUser?.type, hasUserEdit, hasUserDelete, currentUser?.id);

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };
  const rows = users
    ? users?.map((user, index) => ({
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        id: user?.id,
        firstName: `${user?.firstName} ${user?.lastName}${user?.id === currentUser?.id ? " (You)" : ""}`,
        username: user?.username,
        primaryPhone: user?.primaryPhone,
        primaryEmail: user?.primaryEmail,
        roleId: userRoles.find((role) => role.id === user?.roleId)?.name,
        balance: user?.accounts?.[0]?.balance,
        formattedBalance: user?.accounts?.[0]?.formattedBalance,
      }))
    : [];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          // Search data
          placeholder="Search user"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton={hasUserExport}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      {/* Edit User Modal */}
      <AddEditUser userId={userId} open={openModal} onClose={handleCloseModal} onSuccess={onSuccess} />
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this user?"
      />
    </>
  );
};
